import type { MouseEvent } from "react";
import { Container } from "./Container";
import { VisuallyHidden } from "./VisuallyHidden";
import { DESK_TABS, TAB_LABELS, pathForTab } from "../lib/desk-tabs";
import type { DeskTab } from "../lib/desk-tabs";
import styles from "./DeskNav.module.css";

/**
 * The desk's tab row (`APP-016`, `APP-017`).
 *
 * `Submit a deal` shared this bar until `APP-017` moved it into the masthead beside
 * sign-out, which is where `contracts/design/app-desk/` draws it and where the operator
 * asked for it. What is left is navigation alone.
 *
 * This bar still sits below `AppHeader` rather than inside it: the four tabs are a desk
 * structure, and `AppHeader` renders before anybody is signed in.
 *
 * The tabs are anchors with real `href`s, so middle-click, copy-link and "open in new tab"
 * all work. `onSelect` intercepts the ordinary left click and swaps the view in place; see
 * `useDeskTab` for why a real navigation would be wrong here.
 */
export function DeskNav({
  tab,
  onSelect,
}: {
  tab: DeskTab;
  onSelect: (tab: DeskTab) => void;
}) {
  /*
   * Only a plain left click is intercepted. A modified click is a founder asking the browser
   * for a second window, and calling `preventDefault` on it would silently refuse.
   */
  const handle = (next: DeskTab) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onSelect(next);
  };

  return (
    <nav className={styles.nav} aria-label="Seller desk">
      <Container className={styles.inner}>
        <ul className={styles.tabs} role="list">
          {DESK_TABS.map((name) => {
            const current = name === tab;
            return (
              <li key={name}>
                <a
                  href={pathForTab(name)}
                  className={`${styles.tab} ${current ? styles.current : ""}`}
                  /*
                   * `aria-current="page"` and not `aria-selected`: these are links between
                   * addresses, not options in a tablist widget, and announcing them as a
                   * tablist would promise arrow-key semantics anchors do not have.
                   */
                  {...(current ? { "aria-current": "page" as const } : {})}
                  onClick={handle(name)}
                >
                  {/* The short label is what the canvas draws at 390, where four full
                      labels do not fit. Only one tab actually shortens, so only that one
                      pays for the swap: the others render their word once, and the full
                      label stays the accessible name at every width either way. */}
                  {TAB_LABELS[name].short === TAB_LABELS[name].full ? (
                    TAB_LABELS[name].full
                  ) : (
                    <>
                      <span aria-hidden="true" className={styles.short}>
                        {TAB_LABELS[name].short}
                      </span>
                      <span aria-hidden="true" className={styles.full}>
                        {TAB_LABELS[name].full}
                      </span>
                      <VisuallyHidden>{TAB_LABELS[name].full}</VisuallyHidden>
                    </>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </Container>
    </nav>
  );
}
